import React, { ReactNode, useCallback, useEffect, useReducer, useRef } from "react";
import { userReducer } from "context/reducers/userReducer";
import { UserState } from "models/UserState";
import { AuthContext } from "context/auth/AuthContext";
import { publicRoutes } from "constants/publicRoutes";
import { initialLoggedInUserState } from "context/auth/initialLoggedInUserState";
import { useGetMyProfile } from "hooks/useGetMyProfile";
import { switchWorkspace } from "api/services/workspace-service";
import { useGetMyProfilePhoto } from "hooks/useGetMyProfilePhoto";
import { setAccessTokenGetter } from "api/config/axios";

interface AuthProviderProps {
    children: ReactNode;
}

const initialState: UserState = {
  isAuthenticated: false,
  user: initialLoggedInUserState,
  token: null,
  loading: true,
  error: null,
};
  
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [userState, userDispatch] = useReducer(userReducer, initialState);
    const { getMyProfile, gettingMyProfile } = useGetMyProfile();
    const { getMyProfilePhotoUrl } = useGetMyProfilePhoto();
    const workspaceChangeListeners = useRef<Set<() => void>>(new Set());
    
    const refreshMyProfile = useCallback(async () => {
      try {
        const resp = await getMyProfile();
        if (resp?.success && resp.data) {
          const profile = resp.data;
          profile.selectedWorkspace.userPhotoUrl = await getMyProfilePhotoUrl();
          userDispatch({ type: 'REFRESH_PROFILE', payload: resp.data });
        }

      } catch (error) {
        userDispatch({ type: 'LOGIN_FAILURE', payload: error instanceof Error ? error.message : String(error) });
      }
    }, [userDispatch, getMyProfile]);

    const changeWorkspace = useCallback(async (newWorkspaceId: string) => {
        const selectedWorkspace = userState.user.associatedWorkspaces.find(workspace => workspace.id === newWorkspaceId);
        if (userState.user.userId) {
            await switchWorkspace({ workspaceId: selectedWorkspace?.id ?? '' });
            refreshMyProfile();

            workspaceChangeListeners.current.forEach(callback => callback());
        }
    }, [refreshMyProfile, userState.user.associatedWorkspaces, userState.user.userId]);

    const onWorkspaceChanged = useCallback((callback: () => void): () => void => {
        workspaceChangeListeners.current.add(callback);

        return () => {
            workspaceChangeListeners.current.delete(callback);
        };
    }, []);

    useEffect(() => {
      const shouldFetchProfile = !gettingMyProfile && !userState.user.userId && !publicRoutes.includes(window.location.pathname);

      if (shouldFetchProfile) {
        refreshMyProfile();
      }
    }, [getMyProfile, userState.user.userId, refreshMyProfile, gettingMyProfile]);

    useEffect(() => {
      setAccessTokenGetter(() => userState.token);
    }, [userState.token]);
  
    return (
      <AuthContext.Provider value={{ userState, userDispatch, refreshMyProfile, changeWorkspace, onWorkspaceChanged }}>
        {children}
      </AuthContext.Provider>
    );
};